import { Close } from '@mui/icons-material'
import { Box, CircularProgress, IconButton, Portal, Slide, Typography } from '@mui/material'
import { useState } from 'react'
import { useAuth } from '../../../hooks/use-auth'
import { useComments } from '../../../hooks/use-comments'
import { CommentsList } from './CommentsList'
import { type CommentParentType } from '../../../models/Comment'
import { ToggleCommentsButton } from './ToggleCommentsButton'
import { useSearchParams } from 'react-router-dom'

interface CommentsProps {
  parentId: number
  parentType: CommentParentType
  showComments?: boolean
}

export const Comments = (props: CommentsProps): JSX.Element => {
  const auth = useAuth()
  const { comments, setComments, loading } = useComments(props.parentId, props.parentType)

  const [showComments, setShowComments] = useState(props.showComments ?? false)

  const [searchParams, setSearchParams] = useSearchParams()

  const openComments = (e: React.MouseEvent): void => {
    e.stopPropagation()
    setShowComments(true)
  }

  if (auth.user === null) return <></>

  const sortedComments = comments.sort((a, b) => new Date(b.dateStamp).getTime() - new Date(a.dateStamp).getTime())

  const handleCloseComments = (e: React.MouseEvent): void => {
    e.stopPropagation()

    if (searchParams.has('comments')) {
      searchParams.delete('comments')
      setSearchParams(searchParams)
    }

    setShowComments(false)
  }

  return <>
    <ToggleCommentsButton
      unresolvedCommentsCount={comments.filter(c => !c.isResolved).length}
      onClick={openComments}
    />

    <Portal container={document.body}>
      <Slide direction='left' in={showComments} mountOnEnter unmountOnExit>
        <Box
          onClick={(e) => { e.stopPropagation() }}
          data-testid='comments'
          sx={{
            borderRadius: '12px 0 0 12px',
            position: 'fixed',
            top: '0',
            bottom: '0',
            right: '0',
            backgroundColor: 'var(--grey-100)',
            maxWidth: '450px',
            width: '100%',
            padding: '22px 32px',
            display: 'flex',
            flexDirection: 'column',
            gap: '34px',
            margin: '95px 0 29px 0',
            boxShadow: '0px 4px 4px 0px #00000040',
            zIndex: '10'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='h3'>
              Comments
            </Typography>

            <IconButton onClick={handleCloseComments} data-testid='closeCommentsButton' aria-label='close comments'>
              <Close />
            </IconButton>
          </Box>

          {loading
            ? <CircularProgress />
            : <CommentsList
                comments={sortedComments}
                setComments={setComments}
                currentUser={auth.user}
                parentId={props.parentId}
                parentType={props.parentType}
            />
          }
        </Box>
      </Slide>
    </Portal>
  </>
}
